<template>
    <Slide :title="title" :subtitle="subtitle">
        <div class="cr:grid cr:grid-cols-1 cr:md:grid-cols-2 cr:gap-5">
            <div>
                <p>First Name*</p>
                <CustomInput
                    :placeholder="'Enter first name'"
                    v-model="form.firstName"
                    :name="'first-name'"
                    :autocomplete="'given-name'"
                />
            </div>
            <div>
                <p>Last Name*</p>
                <CustomInput
                    :placeholder="'Enter last name'"
                    v-model="form.lastName"
                    :name="'last-name'"
                    :autocomplete="'family-name'"
                />
            </div>
            <div>
                <p>Phone Number*</p>
                <CustomInput
                    :placeholder="'212 555 0188'"
                    v-model="form.phone"
                    :input-type="'tel'"
                    :autocomplete="'tel'"
                    :name="'phone'"
                />
            </div>
            <div>
                <p>Email*</p>
                <CustomInput
                    :placeholder="'name@example.com'"
                    v-model="form.email"
                    :name="'email'"
                    :autocomplete="'email'"
                    :input-type="'email'"
                />
                <p v-if="emailError" class="cr:text-red-500 cr:text-sm cr:mt-1">{{ emailError }}</p>
            </div>
            <div>
                <p>Company Name*</p>
                <CustomInput
                    :placeholder="'ACME Roofing'"
                    v-model="form.companyName"
                />
            </div>
            <div>
                <p>Website*</p>
                <CustomInput
                    :placeholder="'example.com'"
                    v-model="form.website"
                    :input-type="'url'"
                    :autocomplete="'url'"
                    :name="'website'"
                />
            </div>
            <div class="cr:md:col-span-2">
                <p>Job Role</p>
                <CustomInput
                    :placeholder="'Owner'"
                    v-model="form.jobRole"
                />
            </div>
        </div>
    </Slide>
</template>

<script setup lang="ts">
import {ref, computed} from "vue";
import Slide from "@/components/slides/Slide.vue";
import CustomInput from "@/components/elements/CustomInput.vue";
import Validation from "@/services/Validation.ts";
import {useErrorStore} from "@/stores/error.ts";

const props = defineProps({
    title: {
        type: String,
        default: 'Get Started'
    },
    subtitle: {
        type: String,
        default: 'Create your account to start purchasing leads'
    }
});

const errorStore = useErrorStore();

const form = ref({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    companyName: '',
    website: '',
    jobRole: '',
});

const emailError = ref<string | null>(null);

const canContinue = computed(() => true);

const slidePayload = computed(() => ({
    first_name:   form.value.firstName,
    last_name:    form.value.lastName,
    phone:        form.value.phone,
    email:        form.value.email,
    company_name: form.value.companyName,
    website:      form.value.website,
    job_role:     form.value.jobRole || undefined,
}));

const setEmailError = (message: string | null) => {
    emailError.value = message;
};

const validate = (): boolean => {
    emailError.value = null;

    if (!form.value.firstName || form.value.firstName.length < 2) {
        errorStore.report('Please enter a valid first name.');
        return false;
    }
    if (!form.value.lastName || form.value.lastName.length < 2) {
        errorStore.report('Please enter a valid last name.');
        return false;
    }
    if (!form.value.phone || !Validation.validPhone(form.value.phone)) {
        errorStore.report('Please enter a valid US phone number.');
        return false;
    }
    if (!form.value.email || !Validation.validEmail(form.value.email)) {
        errorStore.report('Please enter a valid email address.');
        return false;
    }
    if (!form.value.companyName || form.value.companyName.length < 2) {
        errorStore.report('Please enter a valid company name.');
        return false;
    }
    if (!form.value.website || !Validation.validUrl(form.value.website)) {
        errorStore.report('Please enter a valid website.');
        return false;
    }

    return true;
};

defineExpose({canContinue, slidePayload, validate, setEmailError});
</script>
